import { useAuthStore } from '@/stores/authStore';
import { usePricingData } from '@/hooks/usePricingData';
import { useMemo } from 'react';

export const useAuthProtection = () => {
  const { userData } = useAuthStore();
  const { pricingData, loading: pricingLoading } = usePricingData();

  const isLoading = pricingLoading;

  const canCreateNote = useMemo(() => {
    if (isLoading) {
      // While loading, we can't make a determination.
      // We'll return true to prevent the UI from showing a premature "upgrade" state.
      // The UI component should use the `isLoading` flag to show a loading indicator.
      return true;
    }

    // Check lifetime cap plans first - they don't need pricingData
    // This handles admin-assigned lifetime plans which don't have Stripe price_id/product_id
    if (userData?.subscription?.plan_type === 'lifetime_cap') {
      const activeVideosUsed = userData.subscription.active_videos_used || 0;
      const maxActiveVideos = userData.subscription.max_active_videos || 0;

      // If maxActiveVideos is 0, it means unlimited
      return maxActiveVideos === 0 || activeVideosUsed < maxActiveVideos;
    }

    // For rate-based plans, we need pricingData to determine limits
    if (userData?.subscription && pricingData) {
      const currentPriceId = userData.subscription.price_id;
      const currentProductId = userData.subscription.product_id;

      // Try to find by price_id first, then by product_id (for free plan)
      const product = Object.values(pricingData).find(
        (tier) =>
          (currentPriceId &&
            (tier.prices?.month?.stripePriceId === currentPriceId ||
              tier.prices?.year?.stripePriceId === currentPriceId)) ||
          (currentProductId && tier.product_id === currentProductId),
      );

      if (product) {
        // For rate-based plans, use existing logic
        const currentNoteCount = userData.subscription.current_period_video_count || 0;
        const currentPlanMonthlyVideos = product.monthlyVideos || 0;

        // If monthlyVideos is 0, it means unlimited
        return currentPlanMonthlyVideos === 0 || currentNoteCount < currentPlanMonthlyVideos;
      }
    }

    // Default to free plan limit (10 videos/month)
    const currentNoteCount = userData?.subscription?.current_period_video_count || 0;
    return currentNoteCount < 10;
  }, [pricingData, userData, isLoading]);

  return {
    canCreateNote,
    isLoading,
  };
};
